import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { GraduationCap, Calendar, Lock, Check, Users, Sparkles, ChevronDown, AlertTriangle, Video, FileText, Clock } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";

const TIPS = [
  "Not everyone needs to attend a mentor session — spread the work instead. Send the team member whose question is sharpest.",
  "Come with a sharp question — mentors give better steer when they know what you're optimising for.",
  "Share context before opinions — give the mentor 60 seconds of background and decision options.",
  "Take notes — your CEO Report and Journal benefit from one well-captured mentor insight per day.",
  "Ask for a second perspective — invite your mentor to challenge your thinking, not just validate it.",
  "Close the loop — tell your mentor what you did with their advice. They remember the ones who follow through.",
];

// Mon 15th June 2026
function formatLongDate(input) {
  if (!input) return "";
  let d;
  if (typeof input === "string") {
    // Handle "YYYY-MM-DD" or ISO timestamp
    const m = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    } else {
      d = new Date(input);
    }
  } else {
    d = new Date(input);
  }
  if (Number.isNaN(d.getTime())) return String(input);
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dom = d.getDate();
  const ord = (n) => {
    if (n >= 11 && n <= 13) return "th";
    const last = n % 10;
    return last === 1 ? "st" : last === 2 ? "nd" : last === 3 ? "rd" : "th";
  };
  return `${days[d.getDay()]} ${dom}${ord(dom)} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtHHMM(iso) {
  if (!iso) return "";
  return iso.slice(11, 16);
}

function isPast(slotStartIso) {
  if (!slotStartIso) return false;
  const dt = new Date(slotStartIso.endsWith("Z") || slotStartIso.includes("+") ? slotStartIso : slotStartIso + "Z");
  return !Number.isNaN(dt.getTime()) && dt.getTime() < Date.now();
}

export default function MentorBooking() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [slots, setSlots] = useState([]);
  const [days, setDays] = useState([]);
  const [filterDay, setFilterDay] = useState("");
  const [showPast, setShowPast] = useState(false);
  const [allowance, setAllowance] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "faculty";
  const isMentor = user?.role === "mentor";
  const isStudent = user?.role === "student";
  const myTeamId = user?.team_id;

  const load = async () => {
    const params = filterDay ? `?day_id=${filterDay}` : "";
    const [s, d, a] = await Promise.all([
      api.get(`/mentor-slots${params}`).then((r) => r.data),
      api.get("/days").then((r) => r.data),
      isStudent ? api.get("/mentor-slots/my-allowance").then((r) => r.data).catch(() => null) : Promise.resolve(null),
    ]);
    setSlots(s);
    setDays(d);
    setAllowance(a);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterDay]);

  const grouped = useMemo(() => {
    const by = {};
    slots.forEach((s) => {
      if (!showPast && isPast(s.slot_end || s.slot_start)) return;
      const key = `${s.day_date}__${s.day_id}`;
      if (!by[key]) by[key] = { day_date: s.day_date, day_id: s.day_id, slots: [] };
      by[key].slots.push(s);
    });
    return Object.values(by).sort((a, b) => (a.day_date || "").localeCompare(b.day_date || ""));
  }, [slots, showPast]);

  const book = async (slot) => {
    setBusy(true);
    try {
      await api.post(`/mentor-slots/${slot.id}/book`, { notes });
      toast.success(`Booked ${slot.mentor_name} — ${formatLongDate(slot.day_date)} ${fmtHHMM(slot.slot_start)}`, {
        description: "The mentor will see your booking in their dashboard right away.",
      });
      setBookingId(null);
      setNotes("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not book");
      load();
    } finally { setBusy(false); }
  };

  const bookReserve = async (slot) => {
    if (!(await confirm({
      title: `Take the reserve on ${slot.mentor_name} · ${fmtHHMM(slot.slot_start)}?`,
      description: "If the booked team releases early or doesn't show up, you'll be promoted in and get the meeting link instantly. You can drop your reserve at any time.",
      confirmText: "Yes, take the reserve",
    }))) return;
    setBusy(true);
    try {
      await api.post(`/mentor-slots/${slot.id}/book-reserve`, { notes });
      toast.success("You're on the reserve list", {
        description: "Keep this tab open — you'll get a notification if the slot opens up.",
      });
      setBookingId(null);
      setNotes("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reserve");
      load();
    } finally { setBusy(false); }
  };

  const cancelReserve = async (slot) => {
    if (!(await confirm({ title: `Drop your reserve on ${slot.mentor_name} ${fmtHHMM(slot.slot_start)}?`, destructive: true }))) return;
    setBusy(true);
    try {
      await api.post(`/mentor-slots/${slot.id}/cancel-reserve`);
      toast.success("Reserve dropped");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not drop reserve");
    } finally { setBusy(false); }
  };

  const release = async (slot) => {
    if (!(await confirm({ title: `Release booking on ${slot.mentor_name} ${formatLongDate(slot.day_date)} ${fmtHHMM(slot.slot_start)}?`, destructive: true }))) return;
    setBusy(true);
    try {
      await api.post(`/mentor-slots/${slot.id}/release`);
      toast.success("Slot released");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not release");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="mentor-booking-page">
      <div>
        <div className="label-eyebrow flex items-center gap-2"><GraduationCap size={12} /> Mentor booking · 30-minute slots</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Book time with your mentor</h1>
        <p className="text-[#525252] mt-3 max-w-3xl text-sm leading-relaxed">
          Your team has <strong>one assigned mentor</strong> for the whole programme. They publish the times they can offer;
          you book from those. If your mentor also has a second team and a slot is already taken, take the <strong>reserve</strong> —
          you'll be promoted in automatically if the booked team releases early or doesn't show up.
        </p>
        <p className="text-xs text-[#525252] mt-2">
          Online mentoring runs <strong>5 Nov – 8 Dec</strong>. Sessions on 29 Oct and 4 Nov are in person. Spread the work — not every team member needs to attend.
        </p>
      </div>

      {isStudent && allowance && (
        allowance.assigned ? (
          <div className="border-2 border-[#25AAAA] bg-[#E6F7F7] p-5 flex flex-col md:flex-row md:items-center gap-5" data-testid="my-mentor-card">
            {allowance.mentor?.photo_url ? (
              <img src={allowance.mentor.photo_url} alt={allowance.mentor.name} className="w-16 h-16 object-cover border border-[#E5E7EB] shrink-0" />
            ) : (
              <div className="w-16 h-16 bg-[#25AAAA] text-white flex items-center justify-center text-xl font-bold shrink-0">{allowance.mentor?.name?.[0]}</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="label-eyebrow !text-[#0F5A5A]">Your mentor</div>
              <div className="font-display text-2xl font-black tracking-tighter text-[#0F1F2E]" data-testid="my-mentor-name">{allowance.mentor?.name}</div>
              {allowance.mentor?.expertise && <div className="text-xs text-[#525252] mt-0.5">{allowance.mentor.expertise}</div>}
            </div>
            <div className="md:w-72" data-testid="my-mentor-allowance">
              {(() => {
                const used = allowance.team_used_min || 0, cap = allowance.team_allowance_min || 480;
                const fmt = (m) => `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}`;
                const over = used > cap;
                return (
                  <>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#525252]">Your team's online time</span>
                      <span className={`font-semibold ${over ? "text-[#A23232]" : "text-[#0F1F2E]"}`}>{fmt(used)} of {fmt(cap)}</span>
                    </div>
                    <div className="h-2 bg-white border border-[#25AAAA] mt-1.5"><div className={`h-full ${over ? "bg-[#e93e36]" : "bg-[#25AAAA]"}`} style={{ width: `${Math.min(100, Math.round(used / cap * 100))}%` }} /></div>
                    <div className="text-[10px] text-[#525252] mt-1.5">
                      {over ? "You've gone past your team's allowance — extra time is at your mentor's discretion, so check with them." :
                        allowance.teams_count > 1 ? `Your mentor's 8 hours are shared across ${allowance.teams_count} teams.` : "Your mentor's full 8 hours are yours."}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-[#f4b847] bg-[#FEF3D0] p-6" data-testid="mentor-not-assigned">
            <div className="label-eyebrow text-[#7A5B17]">Not yet</div>
            <div className="font-display text-2xl font-black tracking-tighter text-[#7A5B17] mt-1">Your mentor will be assigned at Session 2.</div>
            <p className="text-sm text-[#7A5B17] mt-2 max-w-xl">Once the programme team pairs your team with a mentor, their available times appear here and you can book straight away.</p>
          </div>
        )
      )}

      {/* Get the best from your mentor */}
      <div className="border border-[#E5E7EB] bg-white" data-testid="mentor-tips-card">
        <button
          onClick={() => setTipsOpen((s) => !s)}
          className="w-full flex items-center justify-between p-4 hover:bg-[#FAFAFA]"
          data-testid="mentor-tips-toggle"
        >
          <div className="flex items-center gap-3">
            <Sparkles size={16} className="text-[#2596be]" />
            <div className="text-left">
              <div className="label-eyebrow">Get the best from your mentor</div>
              <div className="text-sm font-semibold mt-1">6 quick tips for high-leverage mentoring conversations</div>
            </div>
          </div>
          <ChevronDown size={16} className={`transition-transform ${tipsOpen ? "rotate-180" : ""}`} />
        </button>
        {tipsOpen && (
          <div className="px-6 pb-6 pt-2 border-t border-[#E5E7EB]">
            <ol className="space-y-2 text-sm text-[#0A0A0A] list-decimal list-inside">
              {TIPS.map((t, i) => <li key={i} className="leading-relaxed">{t}</li>)}
            </ol>
          </div>
        )}
      </div>

      {/* Day filter */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="mentor-booking-filters">
        <button
          onClick={() => setFilterDay("")}
          className={`text-xs px-3 py-2 border ${!filterDay ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB] hover:border-[#0A0A0A]"}`}
          data-testid="mentor-day-filter-all"
        >All dates</button>
        {days.filter((d) => slots.some((s) => s.day_id === d.id) || filterDay === d.id).sort((a, b) => (a.day_number || 0) - (b.day_number || 0)).map((d) => (
          <button
            key={d.id}
            onClick={() => setFilterDay(d.id)}
            className={`text-xs px-3 py-2 border ${filterDay === d.id ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB] hover:border-[#0A0A0A]"}`}
            data-testid={`mentor-day-filter-${d.day_number}`}
          >Day {d.day_number} · {d.date?.slice(5)}</button>
        ))}
        <label className="ml-auto text-xs text-[#525252] inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} data-testid="mentor-show-past-toggle" /> Show past slots
        </label>
      </div>

      {grouped.length === 0 ? (
        <div className="border border-dashed border-[#E5E7EB] p-8 text-center text-sm text-[#525252]" data-testid="mentor-slots-empty">
          {isStudent && allowance?.assigned ? "Your mentor hasn't published any upcoming times yet — check back soon, or drop them a note in Team Notes." : "No mentor slots in this view."}
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map((g) => (
            <div key={g.day_date}>
              <div className="label-eyebrow flex items-center gap-2 mb-3">
                <Calendar size={12} /> {formatLongDate(g.day_date)}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {g.slots.sort((a, b) => (a.slot_start || "").localeCompare(b.slot_start || "") || (a.mentor_name || "").localeCompare(b.mentor_name || "")).map((s) => {
                  const booked = !!s.booked_by_team_id;
                  const mine = booked && s.booked_by_team_id === myTeamId;
                  const reserveMine = s.reserve_by_team_id && s.reserve_by_team_id === myTeamId;
                  const reserveTaken = !!s.reserve_by_team_id;
                  const time = `${fmtHHMM(s.slot_start)}–${fmtHHMM(s.slot_end)}`;
                  const past = isPast(s.slot_start);
                  const canTakeReserve = booked && !mine && !reserveTaken && !past && !isAdmin && !isMentor && myTeamId;
                  return (
                    <div
                      key={s.id}
                      className={`border bg-white p-4 flex flex-col gap-3 ${
                        s.no_show ? "border-[#A23232] bg-[#FDEDED]"
                        : mine ? "border-[#2596be]"
                        : reserveMine ? "border-[#f4b847] bg-[#FEF3D0]"
                        : booked ? "border-[#E5E7EB] opacity-80"
                        : "border-[#E5E7EB] hover:border-[#0A0A0A]"
                      }`}
                      data-testid={`mentor-slot-${s.id}`}
                    >
                      <div className="flex items-center gap-2">
                        {s.mentor_photo_url ? (
                          <img src={s.mentor_photo_url} alt={s.mentor_name} className="w-8 h-8 object-cover border border-[#E5E7EB]" />
                        ) : (
                          <div className="w-8 h-8 bg-[#0A0A0A] text-white text-xs flex items-center justify-center font-bold">{s.mentor_name?.[0]}</div>
                        )}
                        <div className="text-sm font-semibold">{s.mentor_name}</div>
                      </div>
                      <div className="text-xs text-[#525252] flex items-center gap-1.5">
                        <Clock size={11} /> {time}
                      </div>
                      {s.no_show && (
                        <div className="text-[10px] uppercase tracking-wider bg-[#A23232] text-white px-2 py-1 inline-flex items-center gap-1 self-start" data-testid={`mentor-slot-no-show-${s.id}`}>
                          <AlertTriangle size={11} /> No-show
                        </div>
                      )}
                      {booked && (
                        <div className="text-xs flex items-center gap-1" data-testid={`mentor-slot-state-${s.id}`}>
                          {mine ? (
                            <span className="text-[#1e7da3] inline-flex items-center gap-1"><Check size={11} /> Your team</span>
                          ) : (
                            <span className="text-[#525252] inline-flex items-center gap-1"><Lock size={11} /> {s.team_name || "Booked"}</span>
                          )}
                        </div>
                      )}
                      {reserveTaken && (
                        <div className="text-[10px] uppercase tracking-wider flex items-center gap-1" data-testid={`mentor-slot-reserve-state-${s.id}`}>
                          <Clock size={11} className="text-[#7A5B17]" />
                          {reserveMine ? (
                            <span className="bg-[#f4b847] text-[#0A0A0A] px-2 py-0.5 font-bold">YOUR RESERVE</span>
                          ) : (
                            <span className="text-[#7A5B17]">Reserve: {s.reserve_team_name || "another team"}</span>
                          )}
                        </div>
                      )}
                      {mine && (s.meeting_link || s.mentor_default_meeting_link) && (
                        <a
                          href={s.meeting_link || s.mentor_default_meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-[#2596be] hover:text-[#1e7da3] font-semibold"
                          data-testid={`mentor-slot-meeting-link-${s.id}`}
                        >
                          <Video size={12} /> Join meeting
                        </a>
                      )}
                      {mine && s.session_note && (
                        <div className="bg-[#FEF3D0] border-l-2 border-[#f4b847] p-2 text-xs whitespace-pre-wrap" data-testid={`mentor-slot-note-${s.id}`}>
                          <div className="label-eyebrow text-[#7A5B17] text-[9px] mb-1">Mentor's note</div>
                          {s.session_note}
                        </div>
                      )}
                      {mine && s.recording_summary && (
                        <div className="bg-[#E6F4FA] border-l-2 border-[#2596be] p-2 text-xs whitespace-pre-wrap" data-testid={`mentor-slot-recording-${s.id}`}>
                          <div className="label-eyebrow text-[#1e7da3] text-[9px] mb-1 flex items-center gap-1">
                            <FileText size={10} /> Session summary
                          </div>
                          {s.recording_summary}
                          {s.recording_url && (
                            <a href={s.recording_url} target="_blank" rel="noopener noreferrer" className="block mt-1 text-[#2596be] font-semibold">Recording →</a>
                          )}
                        </div>
                      )}
                      {!booked && !isAdmin && !isMentor && myTeamId && (
                        past ? (
                          <div className="text-[10px] text-[#525252]" data-testid={`mentor-slot-past-${s.id}`}>Past</div>
                        ) : bookingId === s.id ? (
                          <div className="space-y-2">
                            <Textarea
                              className="rounded-none min-h-[60px] text-xs"
                              placeholder="What do you want to discuss? (optional — shared with the mentor in their notification)"
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              data-testid={`mentor-slot-notes-${s.id}`}
                            />
                            <div className="flex gap-2">
                              <Button onClick={() => book(s)} disabled={busy} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3] flex-1 h-8 text-xs" data-testid={`mentor-slot-confirm-${s.id}`}>
                                Confirm
                              </Button>
                              <Button onClick={() => { setBookingId(null); setNotes(""); }} variant="outline" className="rounded-none h-8 text-xs" data-testid={`mentor-slot-cancel-${s.id}`}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            onClick={() => { setBookingId(s.id); setNotes(""); }}
                            disabled={busy}
                            className="rounded-none bg-[#2596be] hover:bg-[#1e7da3] h-8 text-xs"
                            data-testid={`mentor-slot-book-${s.id}`}
                          >
                            Book this slot
                          </Button>
                        )
                      )}
                      {mine && !past && (
                        <Button
                          onClick={() => release(s)}
                          disabled={busy}
                          variant="outline"
                          className="rounded-none h-8 text-xs"
                          data-testid={`mentor-slot-release-${s.id}`}
                        >
                          Release
                        </Button>
                      )}
                      {canTakeReserve && (
                        <Button
                          onClick={() => bookReserve(s)}
                          disabled={busy}
                          variant="outline"
                          className="rounded-none h-8 text-xs border-[#f4b847] text-[#7A5B17] hover:bg-[#FEF3D0]"
                          data-testid={`mentor-slot-book-reserve-${s.id}`}
                        >
                          <Clock size={11} className="mr-1" /> Take reserve
                        </Button>
                      )}
                      {reserveMine && !past && (
                        <Button
                          onClick={() => cancelReserve(s)}
                          disabled={busy}
                          variant="outline"
                          className="rounded-none h-8 text-xs border-[#7A5B17] text-[#7A5B17] hover:bg-[#FEF3D0]"
                          data-testid={`mentor-slot-drop-reserve-${s.id}`}
                        >
                          Drop reserve
                        </Button>
                      )}
                      {!myTeamId && !isAdmin && !isMentor && !booked && (
                        <div className="text-[10px] text-[#525252] flex items-center gap-1" data-testid={`mentor-slot-need-team-${s.id}`}>
                          <Users size={10} /> Join a team to book
                        </div>
                      )}
                      {isAdmin && booked && (
                        <Button
                          onClick={() => release(s)}
                          disabled={busy}
                          variant="outline"
                          className="rounded-none h-8 text-xs border-[#A23232] text-[#A23232]"
                          data-testid={`mentor-slot-admin-release-${s.id}`}
                        >
                          Admin release
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
